import { useCallback, useEffect, useState } from 'react';

import { MAX_RECENTS, STORAGE_KEY } from './constants';
import type { RecentEntry, UseSearchRecentsReturn } from './types';

const isRecentEntry = (value: unknown): value is RecentEntry => {
    if (value === null || typeof value !== 'object') {
        return false;
    }

    const candidate = value as Record<string, unknown>;

    return (
        typeof candidate.id === 'string' &&
        typeof candidate.primary === 'string' &&
        typeof candidate.secondary === 'string' &&
        typeof candidate.lat === 'number' &&
        typeof candidate.lng === 'number'
    );
};

// Parse defensively — any bad shape (non-array, missing fields, old schema)
// quietly falls back to an empty list rather than crashing the drawer.
const readFromStorage = (): RecentEntry[] => {
    if (typeof window === 'undefined') {
        return [];
    }

    try {
        const raw = window.localStorage.getItem(STORAGE_KEY);

        if (raw === null) {
            return [];
        }

        const parsed = JSON.parse(raw) as unknown;

        if (!Array.isArray(parsed)) {
            return [];
        }

        return parsed.filter(isRecentEntry).slice(0, MAX_RECENTS);
    } catch (error) {
        console.error('Failed to parse search recents from localStorage', error);

        return [];
    }
};

const writeToStorage = (entries: RecentEntry[]): void => {
    if (typeof window === 'undefined') {
        return;
    }

    try {
        window.localStorage.setItem(STORAGE_KEY, JSON.stringify(entries));
    } catch (error) {
        console.error('Failed to persist search recents to localStorage', error);
    }
};

const useSearchRecents = (): UseSearchRecentsReturn => {
    const [recents, setRecents] = useState<RecentEntry[]>([]);

    useEffect(() => {
        setRecents(readFromStorage());
    }, []);

    const addRecent = useCallback((entry: RecentEntry) => {
        setRecents(previous => {
            const withoutDuplicate = previous.filter(existing => existing.id !== entry.id);
            const next = [entry, ...withoutDuplicate].slice(0, MAX_RECENTS);

            writeToStorage(next);

            return next;
        });
    }, []);

    const clearRecents = useCallback(() => {
        setRecents([]);

        if (typeof window !== 'undefined') {
            window.localStorage.removeItem(STORAGE_KEY);
        }
    }, []);

    return { addRecent, clearRecents, recents };
};

export default useSearchRecents;
